import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { conversationClient, type ConversationMessageStats } from '@/lib/gupshup-client';

/**
 * How long the backend can keep answering with a count that predates a send: it caches counts for
 * 60 s (`MESSAGE_STATS_TTL_SECONDS`), and a message only counts once its delivery receipt is in.
 */
const SETTLE_MS = 75_000;

/** The counts the operator has already been shown for their own sends, held until the server catches up. */
interface SentFloor {
  conversationId: string;
  conversation: number;
  line: number;
  sentAt: number;
}

function withFloor(
  stats: ConversationMessageStats,
  floor: SentFloor | null,
  conversationId: string | undefined,
  dataUpdatedAt: number,
): ConversationMessageStats {
  if (!floor || floor.conversationId !== conversationId || dataUpdatedAt >= floor.sentAt + SETTLE_MS) return stats;

  const lineSent = Math.max(stats.line.sentThisMonth, floor.line);
  return {
    conversation: { sentThisMonth: Math.max(stats.conversation.sentThisMonth, floor.conversation) },
    line: {
      ...stats.line,
      sentThisMonth: lineSent,
      freeTierUsed: Math.min(stats.line.freeTierLimit, lineSent),
    },
  };
}

/**
 * This chat's and its line's delivered outgoing messages this month (C7), for the composer's cost
 * line. On error `stats` is undefined: the composer then shows only the generic cost line.
 *
 * The backend caches the counts for 60 s and only counts delivered messages, so a refetch right after
 * a send still returns the old number. `recordSend` bumps the shown counts at once instead, and they
 * act as a floor until a fetch made after `SETTLE_MS` — scheduled here — is trusted again (which also
 * corrects the count if a send never got delivered).
 */
export function useMessageStats(conversationId: string | undefined, enabled: boolean) {
  const active = enabled && !!conversationId;
  const { data, isError, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['message-stats', conversationId],
    queryFn: async (): Promise<ConversationMessageStats> => {
      const response = await conversationClient.conversations.getMessageStats(conversationId as string);
      return response.data;
    },
    enabled: active,
  });
  const [floor, setFloor] = useState<SentFloor | null>(null);

  const stats = useMemo(
    () => (isError || !data ? undefined : withFloor(data, floor, conversationId, dataUpdatedAt)),
    [isError, data, floor, conversationId, dataUpdatedAt],
  );

  const recordSend = useCallback(() => {
    if (!active || !stats || !conversationId) return;
    setFloor({
      conversationId,
      conversation: stats.conversation.sentThisMonth + 1,
      line: stats.line.sentThisMonth + 1,
      sentAt: Date.now(),
    });
  }, [active, stats, conversationId]);

  // Once the server count can include the last send, fetch it so the floor gives way to it.
  // `refetch` ignores `enabled`, so a disabled query (no line costs to show) must not fetch.
  useEffect(() => {
    if (!floor || !active || floor.conversationId !== conversationId) return;
    const id = setTimeout(() => void refetch(), floor.sentAt + SETTLE_MS - Date.now());
    return () => clearTimeout(id);
  }, [floor, active, conversationId, refetch]);

  return { stats, recordSend };
}
